import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import LaunchIcon from "@mui/icons-material/Launch";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { formatDate } from "../../services/date";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import { Link } from "react-router-dom";
import ActivityActionDrawer from "./ActivityActionDrawer";

export default function ActivitiesList({ data, openActivityId, onActivityOpened, highlightActivityId, scrollToActivity }) {
  const [expandedActivityId, setExpandedActivityId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const userName = JSON.parse(localStorage.getItem("user")).userName;
  const fontFamily = "Red Hat Display";
  
  // Função para verificar o status do prazo da atividade
  const getDeadlineStatus = (dueDate) => {
    if (!dueDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const deadline = new Date(dueDate);
    deadline.setHours(0, 0, 0, 0);
    
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'overdue'; // Atrasado
    } else if (diffDays <= 2) {
      return 'urgent'; // Em cima da hora (2 dias ou menos)
    }
    return null;
  };
  
  // NÃO abrir automaticamente o drawer - apenas preparar dados se necessário
  // O drawer só deve abrir quando o usuário clicar em "Avançar"
  useEffect(() => {
    if (openActivityId && data && data.length > 0) {
      // Apenas limpar o parâmetro da URL após processar
      if (onActivityOpened) {
        onActivityOpened();
      }
    }
  }, [openActivityId, data, onActivityOpened]);

  // Scroll para atividade específica quando highlightActivityId for fornecido
  useEffect(() => {
    if (highlightActivityId && data && data.length > 0 && scrollToActivity) {
      const targetActivity = data.find(activity => activity.id && activity.id === highlightActivityId);
      if (!targetActivity) return;
      
      // Expandir a atividade primeiro
      setExpandedActivityId(targetActivity.id);

      // Aguardar múltiplas vezes até o elemento estar completamente renderizado e expandido
      const performScroll = () => {
        let attempts = 0;
        const maxAttempts = 30;
        
        const tryScroll = () => {
          attempts += 1;
          const element = document.getElementById(`activity-${targetActivity.id}`);
          
          if (element) {
            // Verificar se o accordion está expandido (altura > 150px indica expandido)
            const rect = element.getBoundingClientRect();
            const isExpanded = rect.height > 150;
            
            if (isExpanded || attempts > 15) {
              // Usar scrollIntoView para garantir que funcione
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest' 
              });
              
              return true;
            }
          }
          
          // Tentar novamente se não conseguiu ainda
          if (attempts < maxAttempts) {
            setTimeout(tryScroll, 100);
          }
          return false;
        };
        
        tryScroll();
      };
      
      // Delay inicial para começar a tentar o scroll
      setTimeout(performScroll, 400);
    }
  }, [highlightActivityId, data, scrollToActivity]);

  // Função para controlar expansão do item
  const handleToggleExpand = (activityId) => {
    const isExpanded = expandedActivityId === activityId;
    setExpandedActivityId(isExpanded ? null : activityId);
    
    // Atualizar URL quando item é aberto/fechado
    const url = new URL(window.location);
    if (!isExpanded) {
      url.searchParams.set('activityId', activityId.toString());
      url.searchParams.set('openActivity', 'true');
    } else {
      url.searchParams.delete('activityId');
      url.searchParams.delete('openActivity');
    }
    window.history.pushState({}, '', url);
  };

  const handleOpenDrawer = (activity) => {
    setSelectedActivity(activity);
    setDrawerOpen(true);
    // Atualizar URL para refletir abertura direta da atividade
    const url = new URL(window.location);
    url.searchParams.set('activityId', activity.id.toString());
    url.searchParams.set('openActivity', 'true');
    window.history.pushState({}, '', url);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };
  const titleSx = {
    fontWeight: 700,
    fontSize: "1.15rem",
    fontFamily: fontFamily,
    color: "text.primary",
  };
  const subtitleSx = {
    fontWeight: 500,
    fontSize: "0.95rem",
    fontFamily: fontFamily,
    color: "text.secondary",
  };
  const detailLabelSx = {
    fontWeight: 600,
    fontSize: "0.9rem",
    fontFamily: fontFamily,
    color: "text.secondary",
    minWidth: "140px",
  };
  const detailValueSx = {
    fontFamily: fontFamily,
    fontSize: "0.9rem",
    color: "text.primary",
  };

  return (
    <React.Fragment>
      {!data || data.length === 0 ? (
        <Box sx={{
          height: "100%",
          minHeight: 320,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          color: "text.secondary",
          py: 6,
        }}>
          <SearchOffIcon sx={{ fontSize: 56, opacity: 0.6 }} />
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
            Nenhuma atividade encontrada
          </Typography>
          <Typography sx={{ fontSize: "0.95rem" }}>
            Ajuste sua busca ou filtros para ver resultados.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {data.map((project, index) => {
            let parsedDeadline;

            if (project.dueDate != null) {
              parsedDeadline = formatDate(project.dueDate);
            }

            const isExpanded = expandedActivityId === project.id;
            const isLastItem = index === data.length - 1;
            const deadlineStatus = getDeadlineStatus(project.dueDate);

            return (
              <Box 
                key={project.id} 
                id={`activity-${project.id}`}
                sx={{ 
                  borderBottom: isLastItem ? 'none' : '1px solid #EEEEEE',
                  py: 2.5,
                  px: 1.5,
                  transition: "background-color 0.2s ease",
                  cursor: 'pointer',
                  '&:hover': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
                onClick={() => handleToggleExpand(project.id)}
              >
                {/* Cabeçalho do Item */}
                <Box display="flex" alignItems="center" gap={2}>
                  {/* Título e informações principais */}
                  <Box flex={1} display="flex" alignItems="center" gap={3}>
                    <Box minWidth="180px">
                      <Typography sx={titleSx}>
                        {project.providenceName}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography sx={subtitleSx}>
                          {project.projectNumber} • {project.pdcName}
                        </Typography>
                        <IconButton
                          component={Link}
                          to={`/projects/${project.projectNumber}`}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            ml: 0.5,
                            p: 0.25,
                            '& .MuiSvgIcon-root': {
                              fontSize: '0.9rem',
                              color: 'primary.main',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Chip 
                      label={project.stageName}
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        backgroundColor: 'primary.main',
                        color: 'white',
                      }}
                    />

                    {project.beingRealized === 1 && (
                      <Chip 
                        label={`Em realização${project.performUser === userName ? '' : ` - ${project.performUser}`}`}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    )}

                    {deadlineStatus === 'overdue' && (
                      <Chip 
                        label="Atrasado"
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: '#d32f2f',
                          color: 'white',
                        }}
                      />
                    )}

                    {deadlineStatus === 'urgent' && (
                      <Chip 
                        label="Em cima da hora"
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: '#f57c00',
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>

                  {/* Ações */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ 
                        borderRadius: 2, 
                        backgroundColor: "#CA9900", 
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { backgroundColor: "#B68700" } 
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDrawer(project);
                      }}
                    >
                      AVANÇAR
                    </Button>
                    
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpand(project.id);
                      }}
                      sx={{ ml: 1 }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Detalhes Expandidos */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 2, pl: 2, pt: 2, borderTop: '1px solid #EEEEEE' }}>
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      <Box display="flex" gap={2}>
                        <Typography sx={detailLabelSx}>Nome:</Typography>
                        <Typography sx={detailValueSx}>{project.name}</Typography>
                      </Box>

                      <Box display="flex" gap={2}>
                        <Typography sx={detailLabelSx}>Integrador:</Typography>
                        <Typography sx={detailValueSx}>{project.clientName}</Typography>
                      </Box>

                      <Box display="flex" gap={2}>
                        <Typography sx={detailLabelSx}>Etapa:</Typography>
                        <Typography sx={detailValueSx}>{project.stageName}</Typography>
                      </Box>

                      <Box display="flex" gap={2}>
                        <Typography sx={detailLabelSx}>Prazo Máximo:</Typography>
                        <Typography sx={detailValueSx}>{parsedDeadline || '—'}</Typography>
                      </Box>

                      {project.note && (
                        <Box display="flex" gap={2}>
                          <Typography sx={detailLabelSx}>Observação:</Typography>
                          <Typography sx={detailValueSx}>{project.note}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      <ActivityActionDrawer open={drawerOpen} onClose={handleCloseDrawer} activity={selectedActivity} />
    </React.Fragment>
  );
}
